/**
 * The ONLY file that talks to Supabase. Moving to another provider later
 * means rewriting this file, not the screens.
 */
import { createClient, type SupabaseClient, type PostgrestError, type User } from '@supabase/supabase-js';
import type { AuthEvent, Backend } from './backend';
import type { AdminActionResult, AdminFamily, AdminMember, AdminUser, AdminUserAction, PasswordStatus, UserInvitePeek, Change, DataRow, DataTable, FamilySnapshot, Invite, InvitePeek, Member, Membership, RemoteEvent, Role, Session } from './types';
import { FAMILY_LIST_TABLES, PATIENT_TABLES, RejectedError, isSettingTable } from './types';
import { catalogKey, cleanCatalog, dayRangeISO, loginEmail } from './monitoring';
import type { CatalogInput, CatalogMedicine, AppDevice, AppEvent, AppLogin, AppLoginAction, AppLoginResult, BpReading, DoseLog, MarkDoseArgs, MonitorSnapshot } from './monitoring';

const ALL_TABLES: DataTable[] = ['patients', ...PATIENT_TABLES, ...FAMILY_LIST_TABLES, 'family_settings', 'user_settings'];
/** Tables added by newer migrations: if the database doesn't have them yet, load without them. */
const OPTIONAL_TABLES = new Set<DataTable>(['vaccinations']);
const isMissingTable = (e: any) => e && (e.code === 'PGRST205' || e.code === '42P01' || /does not exist|schema cache/i.test(String(e.message || '')));
const PAGE = 1000;

function toSession(u: User | null | undefined): Session | null {
  if (!u) return null;
  const meta = (u.user_metadata || {}) as { display_name?: string };
  return { userId: u.id, email: u.email || '', displayName: meta.display_name || (u.email || '').split('@')[0] };
}

/** Network problems are retried; anything the server actively refused is not. */
function fail(error: PostgrestError | { message: string; code?: string; status?: number } | null): never | void {
  if (!error) return;
  const code = (error as PostgrestError).code || '';
  const msg = error.message || 'Unknown error';
  // Postgres/PostgREST refusals (permission 42501, bad reference 23503, raised P0001, PGRST…)
  // are final. Anything else (offline, timeouts, 5xx) is retried later.
  if (/^(2|4|P0|PGRST)/.test(code)) throw new RejectedError(msg, code);
  throw new Error(msg);
}

function friendlyAuthError(e: { message?: string } | null): Error {
  const m = e?.message || 'Something went wrong';
  if (/invalid login credentials/i.test(m)) return new Error('auth.err.invalid');
  if (/email not confirmed/i.test(m)) return new Error('auth.err.unconfirmed');
  if (/already registered|already been registered/i.test(m)) return new Error('auth.err.exists');
  if (/password should be at least/i.test(m)) return new Error('auth.err.weak');
  if (/rate limit|too many/i.test(m)) return new Error('auth.err.rate');
  if (/banned/i.test(m)) return new Error('auth.err.deactivated');
  return new Error(m);
}

export class SupabaseBackend implements Backend {
  readonly mode = 'supabase' as const;
  private sb: SupabaseClient;

  /**
   * `options.storage`: where the login is kept (the mobile app passes its
   * secure storage; the website uses the browser's own storage).
   */
  constructor(url: string, anonKey: string, options: { storage?: any; detectSessionInUrl?: boolean; fetch?: typeof fetch } = {}) {
    this.sb = createClient(url, anonKey, {
      auth: {
        persistSession: true, autoRefreshToken: true,
        detectSessionInUrl: options.detectSessionInUrl ?? (typeof window !== 'undefined' && typeof location !== 'undefined'),
        ...(options.storage ? { storage: options.storage } : {}),
      },
      ...(options.fetch ? { global: { fetch: options.fetch } } : {}),
    });
  }

  // ---------------- auth ----------------
  async getSession() {
    const { data } = await this.sb.auth.getSession();
    return toSession(data.session?.user);
  }
  onAuthChange(cb: (s: Session | null, e: AuthEvent) => void) {
    const { data } = this.sb.auth.onAuthStateChange((event, session) => {
      const e: AuthEvent = event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'PASSWORD_RECOVERY' ? event : 'OTHER';
      cb(toSession(session?.user), e);
    });
    return () => data.subscription.unsubscribe();
  }
  async signUp(email: string, password: string, displayName: string) {
    const { data, error } = await this.sb.auth.signUp({
      email, password,
      options: { data: { display_name: displayName }, emailRedirectTo: `${location.origin}/` },
    });
    if (error) throw friendlyAuthError(error);
    // Supabase returns a user with no identities when the email already exists.
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) throw new Error('auth.err.exists');
    return { needsConfirmation: !data.session };
  }
  async signIn(emailOrUsername: string, password: string) {
    const { error } = await this.sb.auth.signInWithPassword({ email: loginEmail(emailOrUsername), password });
    if (error) throw friendlyAuthError(error);
  }
  async signOut() { await this.sb.auth.signOut(); }
  async sendPasswordReset(email: string) {
    const { error } = await this.sb.auth.resetPasswordForEmail(email, { redirectTo: `${location.origin}/reset-password` });
    if (error) throw friendlyAuthError(error);
  }
  async updatePassword(password: string) {
    const { error } = await this.sb.auth.updateUser({ password });
    if (error) throw friendlyAuthError(error);
  }
  async deleteAccount() {
    const { error } = await this.sb.rpc('delete_my_account');
    if (error) throw new Error(error.message);
    await this.sb.auth.signOut();
  }
  async updateDisplayName(name: string) {
    const { error } = await this.sb.auth.updateUser({ data: { display_name: name } });
    if (error) throw friendlyAuthError(error);
  }

  // ---------------- families ----------------
  private async uid() {
    const s = await this.getSession();
    if (!s) throw new Error('Not signed in');
    return s.userId;
  }
  async listMemberships(): Promise<Membership[]> {
    const uid = await this.uid();
    const { data, error } = await this.sb.from('family_members')
      .select('family_id, role, all_patients, families(name)').eq('user_id', uid);
    fail(error);
    return (data || []).map((r: any) => ({
      familyId: r.family_id, role: r.role as Role, allPatients: r.all_patients,
      familyName: (Array.isArray(r.families) ? r.families[0]?.name : r.families?.name) || 'Family',
    }));
  }
  async createFamily(name: string, firstPatient: Record<string, unknown> | null) {
    const { data, error } = await this.sb.rpc('create_family', { p_name: name, p_first_patient: firstPatient });
    fail(error);
    return data as string;
  }
  async renameFamily(id: string, name: string) {
    fail((await this.sb.from('families').update({ name }).eq('id', id)).error);
  }
  async deleteFamily(id: string) {
    fail((await this.sb.from('families').delete().eq('id', id)).error);
  }
  async listMembers(fid: string): Promise<Member[]> {
    const [{ data, error }, acc] = await Promise.all([
      this.sb.from('family_members').select('*').eq('family_id', fid).order('joined_at'),
      this.sb.from('member_patient_access').select('user_id, patient_id').eq('family_id', fid),
    ]);
    fail(error); fail(acc.error);
    const byUser = new Map<string, string[]>();
    (acc.data || []).forEach((a: any) => byUser.set(a.user_id, [...(byUser.get(a.user_id) || []), a.patient_id]));
    return (data || []).map((m: any) => ({
      userId: m.user_id, email: m.email || '', displayName: m.display_name || m.email || '',
      role: m.role, allPatients: m.all_patients, patientIds: byUser.get(m.user_id) || [], joinedAt: m.joined_at,
    }));
  }
  async updateMember(fid: string, userId: string, patch: { role?: Exclude<Role, 'owner'>; allPatients?: boolean; patientIds?: string[] }) {
    const upd: Record<string, unknown> = {};
    if (patch.role) upd.role = patch.role;
    if (patch.allPatients !== undefined) upd.all_patients = patch.allPatients;
    if (Object.keys(upd).length) fail((await this.sb.from('family_members').update(upd).eq('family_id', fid).eq('user_id', userId)).error);
    if (patch.patientIds) {
      fail((await this.sb.from('member_patient_access').delete().eq('family_id', fid).eq('user_id', userId)).error);
      if (patch.patientIds.length) {
        fail((await this.sb.from('member_patient_access').insert(patch.patientIds.map(p => ({ family_id: fid, user_id: userId, patient_id: p })))).error);
      }
    }
  }
  async removeMember(fid: string, userId: string) {
    fail((await this.sb.from('family_members').delete().eq('family_id', fid).eq('user_id', userId)).error);
  }
  async transferOwnership(fid: string, userId: string) {
    fail((await this.sb.rpc('transfer_ownership', { p_family: fid, p_new_owner: userId })).error);
  }
  async listInvites(fid: string): Promise<Invite[]> {
    const { data, error } = await this.sb.from('family_invites').select('*').eq('family_id', fid)
      .is('accepted_at', null).is('revoked_at', null).order('created_at', { ascending: false });
    fail(error);
    return (data || []).map(mapInvite);
  }
  async createInvite(fid: string, inv: { email: string; role: Exclude<Role, 'owner'>; allPatients: boolean; patientIds: string[] }) {
    const { data, error } = await this.sb.from('family_invites').insert({
      family_id: fid, email: inv.email.trim().toLowerCase(), role: inv.role, all_patients: inv.allPatients, patient_ids: inv.patientIds,
    }).select('*').single();
    fail(error);
    return mapInvite(data);
  }
  async revokeInvite(id: string) {
    fail((await this.sb.from('family_invites').update({ revoked_at: new Date().toISOString() }).eq('id', id)).error);
  }
  async peekInvite(token: string): Promise<InvitePeek | null> {
    const { data, error } = await this.sb.rpc('peek_invite', { p_token: token });
    if (error) return null;
    const r = (data || [])[0];
    return r ? { familyName: r.family_name, invitedEmail: r.invited_email, role: r.role, invitedByName: r.invited_by_name, status: r.status } : null;
  }
  async acceptInvite(token: string) {
    const { data, error } = await this.sb.rpc('accept_invite', { p_token: token });
    if (error) throw new Error(error.message);
    return data as string;
  }

  // ---------------- super admin ----------------
  async isAppAdmin() {
    const { data, error } = await this.sb.rpc('is_app_admin');
    if (error) return false; // migration 3 not run yet
    return data === true;
  }
  async adminListUsers(): Promise<AdminUser[]> {
    const { data, error } = await this.sb.rpc('admin_list_users');
    fail(error);
    return (data || []).map((u: any) => ({
      userId: u.user_id, email: u.email, displayName: u.display_name, createdAt: u.created_at,
      lastSignInAt: u.last_sign_in_at, status: u.status, inviteExpiresAt: u.invite_expires_at, tempExpiresAt: u.temp_expires_at ?? null, isAdmin: u.is_admin, families: u.families || '',
    }));
  }
  async adminListFamilies(): Promise<AdminFamily[]> {
    const { data, error } = await this.sb.rpc('admin_list_families');
    fail(error);
    return (data || []).map((f: any) => ({
      familyId: f.family_id, name: f.name, ownerEmail: f.owner_email || '', patients: Number(f.patients), members: Number(f.members),
    }));
  }
  async adminFamilyMembers(fid: string): Promise<AdminMember[]> {
    const { data, error } = await this.sb.rpc('admin_family_members', { p_family: fid });
    fail(error);
    return (data || []).map((m: any) => ({
      userId: m.user_id, email: m.email || '', displayName: m.display_name || m.email || '', role: m.role, allPatients: m.all_patients,
      patientIds: m.patient_ids || [], joinedAt: m.joined_at, username: m.username ?? null,
    }));
  }
  async adminSetMember(fid: string, userId: string, role: Role, allPatients: boolean, patientIds: string[]) {
    fail((await this.sb.rpc('admin_set_member', { p_family: fid, p_user: userId, p_role: role, p_all: allPatients, p_patient_ids: patientIds })).error);
  }
  async adminAddMember(fid: string, login: string, role: Role, allPatients: boolean, patientIds: string[]) {
    fail((await this.sb.rpc('admin_add_member', { p_family: fid, p_login: login, p_role: role, p_all: allPatients, p_patient_ids: patientIds })).error);
  }
  async adminRemoveMember(fid: string, userId: string) {
    fail((await this.sb.rpc('admin_remove_member', { p_family: fid, p_user: userId })).error);
  }
  async adminCreateFamily(name: string, ownerLogin: string) {
    const { data, error } = await this.sb.rpc('admin_create_family', { p_name: name, p_owner_login: ownerLogin });
    fail(error);
    return data as string;
  }
  private async invoke(fn: string, body: object): Promise<any> {
    const { data, error } = await this.sb.functions.invoke(fn, { body: body as Record<string, unknown> });
    if (error) {
      let msg = error.message;
      try { const ctx = (error as any).context; if (ctx?.json) msg = (await ctx.json()).error || msg; } catch { /* keep msg */ }
      if (/Failed to send|not found|404/i.test(msg)) msg = `The ${fn} server function is not set up yet (see docs/SETUP.md, "Automatic database updates").`;
      throw new Error(msg);
    }
    if (data?.error) throw new Error(data.error);
    return data || {};
  }
  async adminUserAction(a: AdminUserAction): Promise<AdminActionResult> { return this.invoke('admin-users', a); }
  async adminExists() {
    const { data, error } = await this.sb.rpc('admin_exists');
    if (error) return true; // migration not run yet: don't show the setup screen
    return data === true;
  }
  async bootstrapAdmin(name: string, email: string, password: string) {
    await this.invoke('public-auth', { action: 'bootstrap_admin', name, email, password });
  }
  async peekUserInvite(token: string): Promise<UserInvitePeek | null> {
    const { data, error } = await this.sb.rpc('peek_user_invite', { p_token: token });
    if (error) return null;
    return (data || [])[0] || null;
  }
  async acceptUserInvite(token: string, password: string) {
    return this.invoke('public-auth', { action: 'accept_invite', token, password });
  }
  async passwordStatus(): Promise<PasswordStatus> {
    const { data, error } = await this.sb.rpc('my_password_status');
    if (error) return 'none'; // migration not run yet
    return (data as PasswordStatus) || 'none';
  }
  async finishTempPassword(password: string) {
    await this.invoke('public-auth', { action: 'finish_temp_password', password });
    // Fresh tokens so nothing cached from the one-time sign-in is reused.
    await this.sb.auth.refreshSession();
  }

  // ---------------- data ----------------
  async loadFamilyData(fid: string): Promise<FamilySnapshot> {
    const rows: DataRow[] = [];
    await Promise.all(ALL_TABLES.map(async table => {
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await this.sb.from(table).select('*').eq('family_id', fid).range(from, from + PAGE - 1);
        if (error && OPTIONAL_TABLES.has(table) && isMissingTable(error)) { console.warn(`[Bhorosha] table ${table} not found; run the latest migration`); break; }
        if (error) fail(error);
        (data || []).forEach((r: any) => rows.push(fromDb(table, r)));
        if (!data || data.length < PAGE) break;
      }
    }));
    return { rows, fetchedAt: Date.now() };
  }

  async applyChange(fid: string, c: Change) {
    const t = this.sb.from(c.table);
    const uid = c.table === 'user_settings' ? await this.uid() : undefined;
    if (c.upserts.length) {
      const payload = c.upserts.map(u => toDb(fid, c, u, uid));
      const onConflict = c.table === 'family_settings' ? 'family_id,key' : c.table === 'user_settings' ? 'user_id,family_id,key'
        : (PATIENT_TABLES as readonly string[]).includes(c.table) ? 'family_id,patient_id,id' : 'family_id,id';
      for (let i = 0; i < payload.length; i += 500) {
        try { fail((await t.upsert(payload.slice(i, i + 500), { onConflict })).error); }
        catch (e) { throw e instanceof RejectedError ? e : new Error(String(e)); }
      }
    }
    if (c.deletes.length) {
      const idCol = isSettingTable(c.table) ? 'key' : 'id';
      let q = this.sb.from(c.table).delete().eq('family_id', fid);
      if (uid) q = q.eq('user_id', uid);
      if (c.patientId) q = q.eq('patient_id', c.patientId);
      fail((await q.in(idCol, c.deletes)).error);
    }
  }

  subscribe(fid: string, onEvent: (ev: RemoteEvent) => void) {
    const ch = this.sb.channel(`family-${fid}-${Math.random().toString(36).slice(2)}`);
    for (const table of ALL_TABLES) {
      ch.on('postgres_changes' as any, { event: '*', schema: 'public', table, filter: `family_id=eq.${fid}` }, (p: any) => {
        if (p.eventType === 'DELETE') {
          const o = p.old || {};
          // Delete events can't be filtered server-side; ignore other families'.
          if (o.family_id && o.family_id !== fid) return;
          if (o.id === undefined && o.key === undefined) { onEvent({ kind: 'membership' }); return; } // no details: refetch
          onEvent({ kind: 'row', type: 'delete', table, patientId: o.patient_id ?? undefined, id: isSettingTable(table) ? o.key : o.id });
        } else {
          onEvent({ kind: 'row', type: 'upsert', row: fromDb(table, p.new) });
        }
      });
    }
    for (const table of ['family_members', 'member_patient_access']) {
      ch.on('postgres_changes' as any, { event: '*', schema: 'public', table, filter: `family_id=eq.${fid}` }, () => onEvent({ kind: 'membership' }));
    }
    ch.subscribe();
    return () => { void this.sb.removeChannel(ch); };
  }

  // ---------------- mobile app + Daily log ----------------
  async listAppLogins(fid: string): Promise<AppLogin[]> {
    const r = await this.invoke('admin-users', { action: 'family_list_logins', familyId: fid });
    return r.logins || [];
  }
  async appLoginAction(a: AppLoginAction): Promise<AppLoginResult> { return this.invoke('admin-users', a); }

  async loadMonitor(fid: string, fromISO: string, toISO: string, patientIds?: string[]): Promise<MonitorSnapshot> {
    const { from, to } = dayRangeISO(fromISO, toISO);
    const byPatient = (q: any) => (patientIds && patientIds.length ? q.in('patient_id', patientIds) : q);
    const optional = async (q: PromiseLike<{ data: any; error: any }>) => {
      const { data, error } = await q;
      if (error && isMissingTable(error)) return []; // migration not run yet
      fail(error);
      return data || [];
    };
    const [doses, bp, events, devices] = await Promise.all([
      optional(byPatient(this.sb.from('dose_logs').select('*').eq('family_id', fid).gte('dose_date', fromISO).lte('dose_date', toISO)).limit(5000)),
      optional(byPatient(this.sb.from('bp_readings').select('*').eq('family_id', fid).gte('measured_at', from).lt('measured_at', to)).order('measured_at').limit(5000)),
      optional(this.sb.from('app_events').select('*').eq('family_id', fid).gte('at', from).lt('at', to).order('at').limit(5000)),
      optional(this.sb.from('app_devices').select('*').eq('family_id', fid)),
    ]);
    return {
      doses: doses.map(mapDoseLog),
      bp: bp.map(mapBp),
      events: events.filter((e: any) => !patientIds || !patientIds.length || !e.patient_id || patientIds.includes(e.patient_id)).map(mapEvent),
      devices: devices.map(mapDevice),
    };
  }
  async markDose(fid: string, a: MarkDoseArgs) {
    const { data, error } = await this.sb.rpc('mark_dose', {
      p_family: fid, p_patient: a.patientId, p_source: a.source, p_source_id: a.sourceId, p_medicine: a.medicineId,
      p_date: a.date, p_slot: a.slot, p_planned_time: a.plannedTime, p_name: a.name, p_amount: a.amount, p_unit: a.unit,
      p_status: a.status, p_taken_at: a.takenAt ?? null, p_reason: a.reason ?? null, p_note: a.note ?? null,
      p_from: a.from, p_device: a.deviceId ?? null,
    });
    fail(error);
    const onHand = (data as any)?.on_hand;
    return { onHand: onHand === null || onHand === undefined ? null : Number(onHand) };
  }
  async saveBp(fid: string, r: BpReading) {
    fail((await this.sb.from('bp_readings').upsert({
      family_id: fid, patient_id: r.patientId, id: r.id, measured_at: r.measuredAt, systolic: r.systolic, diastolic: r.diastolic,
      pulse: r.pulse, arm: r.arm ?? null, position: r.position ?? null, level: r.level, note: r.note ?? null, logged_from: r.loggedFrom ?? 'mobile',
    }, { onConflict: 'family_id,patient_id,id' })).error);
  }
  async loadBpReadings(fid: string, patientId: string, fromISO: string, toISO: string): Promise<BpReading[]> {
    const { from, to } = dayRangeISO(fromISO, toISO);
    const { data, error } = await this.sb.from('bp_readings').select('*').eq('family_id', fid).eq('patient_id', patientId)
      .gte('measured_at', from).lt('measured_at', to).order('measured_at', { ascending: false }).limit(2000);
    if (error && isMissingTable(error)) return [];
    fail(error);
    return (data || []).map(mapBp);
  }
  async deleteBp(fid: string, patientId: string, id: string) {
    const { data, error } = await this.sb.from('bp_readings').delete().eq('family_id', fid).eq('patient_id', patientId).eq('id', id).select('id');
    fail(error);
    if (!data || !data.length) throw new Error('Only the family owner can remove a reading saved by someone else.');
  }
  async logEvents(fid: string, events: AppEvent[]) {
    if (!events.length) return;
    const { error } = await this.sb.from('app_events').upsert(events.map(e => ({
      family_id: fid, id: e.id, patient_id: e.patientId, device_id: e.deviceId, at: e.at, kind: e.kind, data: e.data || {},
    })), { onConflict: 'family_id,id', ignoreDuplicates: true });
    fail(error);
  }
  async upsertDevice(fid: string, d: AppDevice) {
    const row: Record<string, unknown> = {
      device_id: d.deviceId, family_id: fid, display_name: d.displayName ?? null, platform: d.platform ?? null, model: d.model ?? null,
      app_version: d.appVersion ?? null, notifications: d.notifications ?? null, exact_alarms: d.exactAlarms ?? null,
      battery_optimised: d.batteryOptimised ?? null, push_token: d.pushToken ?? null, last_seen_at: new Date().toISOString(),
      last_sync_at: d.lastSyncAt ?? null, reminders_until: d.remindersUntil ?? null,
    };
    if (d.alertPrefs !== undefined) row.alert_prefs = d.alertPrefs;
    let { error } = await this.sb.from('app_devices').upsert(row, { onConflict: 'user_id,device_id' });
    // Database not updated yet (no alert_prefs column): save the rest.
    if (error && 'alert_prefs' in row && /alert_prefs/.test(error.message || '')) {
      delete row.alert_prefs;
      ({ error } = await this.sb.from('app_devices').upsert(row, { onConflict: 'user_id,device_id' }));
    }
    fail(error);
  }
  async listCatalog(): Promise<CatalogMedicine[]> {
    const out: CatalogMedicine[] = [];
    for (let from = 0; ; from += 1000) {
      const { data, error } = await this.sb.from('medicine_catalog').select('id,name,strength,unit,type,generic,company').order('name').range(from, from + 999);
      if (error) { if (isMissingTable(error)) return []; fail(error); }
      out.push(...((data || []) as CatalogMedicine[]));
      if (!data || data.length < 1000) break;
    }
    return out;
  }
  async adminSaveCatalog(items: CatalogInput[], updateMatches: boolean) {
    const existing = new Map((await this.listCatalog()).map(m => [catalogKey(m), m]));
    const inserts: CatalogInput[] = [], updates: CatalogInput[] = [];
    let skipped = 0;
    const seen = new Set<string>();
    for (const raw of items) {
      const m = cleanCatalog(raw);
      if (!m.name) { skipped++; continue; }
      const key = catalogKey(m);
      if (seen.has(key) && !m.id) { skipped++; continue; }
      seen.add(key);
      const old = m.id ? { id: m.id } : existing.get(key);
      if (old) { if (m.id || updateMatches) updates.push({ ...m, id: old.id }); else skipped++; }
      else inserts.push(m);
    }
    const check = (error: PostgrestError | null) => {
      if (error && isMissingTable(error)) throw new Error('The shared Medicine Library is not set up in the database yet. Run supabase/migrations/20261104000000_medicine_catalog.sql in the Supabase SQL Editor, then try again.');
      fail(error);
    };
    for (let i = 0; i < inserts.length; i += 500) check((await this.sb.from('medicine_catalog').insert(inserts.slice(i, i + 500))).error);
    for (let i = 0; i < updates.length; i += 500) check((await this.sb.from('medicine_catalog').upsert(updates.slice(i, i + 500), { onConflict: 'id' })).error);
    return { added: inserts.length, updated: updates.length, skipped };
  }
  async adminDeleteCatalog(ids: string[]) {
    for (let i = 0; i < ids.length; i += 200) fail((await this.sb.from('medicine_catalog').delete().in('id', ids.slice(i, i + 200))).error);
  }
  async removeDevice(fid: string, userId: string, deviceId: string) {
    // First ask the phone to sign out (it checks at every sync), then take it off the list.
    const req = await this.sb.from('app_device_signouts').upsert({ user_id: userId, device_id: deviceId, family_id: fid, requested_at: new Date().toISOString() }, { onConflict: 'user_id,device_id' });
    if (req.error && !isMissingTable(req.error)) fail(req.error);
    const { data, error } = await this.sb.from('app_devices').delete().eq('family_id', fid).eq('user_id', userId).eq('device_id', deviceId).select('device_id');
    fail(error);
    if (!data || !data.length) throw new Error('Only the family owner can remove a phone.');
  }
  async takeSignOutRequest(deviceId: string): Promise<boolean> {
    const uid = (await this.sb.auth.getSession()).data.session?.user.id;
    if (!uid) return false;
    const { data, error } = await this.sb.from('app_device_signouts').select('device_id').eq('user_id', uid).eq('device_id', deviceId).limit(1);
    if (error) { if (isMissingTable(error)) return false; fail(error); }
    if (!data || !data.length) return false;
    await this.sb.from('app_device_signouts').delete().eq('user_id', uid).eq('device_id', deviceId);
    return true;
  }
  subscribeMonitor(fid: string, onChange: () => void) {
    const ch = this.sb.channel(`monitor-${fid}-${Math.random().toString(36).slice(2)}`);
    for (const table of ['dose_logs', 'bp_readings', 'app_events', 'app_devices', 'medicines']) {
      ch.on('postgres_changes' as any, { event: '*', schema: 'public', table, filter: `family_id=eq.${fid}` }, () => onChange());
    }
    ch.subscribe();
    return () => { void this.sb.removeChannel(ch); };
  }

  async keepAlive() { await this.sb.rpc('ping'); }
}

function fromDb(table: DataTable, r: any): DataRow {
  if (isSettingTable(table)) return { table, id: r.key, position: 0, data: r.value };
  return { table, patientId: r.patient_id ?? undefined, id: r.id, position: r.position ?? 0, data: r.data };
}
function toDb(fid: string, c: Change, u: { id: string; position: number; data: unknown }, uid?: string) {
  if (c.table === 'user_settings') return { user_id: uid, family_id: fid, key: u.id, value: String(u.data) };
  if (c.table === 'family_settings') return { family_id: fid, key: u.id, value: String(u.data) };
  const row: Record<string, unknown> = { family_id: fid, id: u.id, position: u.position, data: u.data };
  if (c.patientId) row.patient_id = c.patientId;
  return row;
}
function mapInvite(r: any): Invite {
  return {
    id: r.id, email: r.email, role: r.role, allPatients: r.all_patients, patientIds: r.patient_ids || [],
    token: r.token, createdAt: r.created_at, expiresAt: r.expires_at, acceptedAt: r.accepted_at, revokedAt: r.revoked_at,
  };
}

function mapDoseLog(r: any): DoseLog {
  return {
    patientId: r.patient_id, id: r.id, source: r.source, sourceId: r.source_id, medicineId: r.medicine_id ?? null,
    doseDate: String(r.dose_date).slice(0, 10), slot: r.slot, plannedTime: r.planned_time ?? null, name: r.name ?? null,
    amount: Number(r.amount) || 0, unit: r.unit ?? null, status: r.status, takenAt: r.taken_at ?? null, reason: r.reason ?? null,
    note: r.note ?? null, loggedBy: r.logged_by ?? null, loggedFrom: r.logged_from ?? null, changes: Array.isArray(r.changes) ? r.changes : [],
    updatedAt: r.updated_at,
  };
}
function mapBp(r: any): BpReading {
  return {
    patientId: r.patient_id, id: r.id, measuredAt: r.measured_at, systolic: r.systolic, diastolic: r.diastolic, pulse: r.pulse ?? null,
    arm: r.arm ?? null, position: r.position ?? null, level: r.level || 'normal', note: r.note ?? null, loggedBy: r.logged_by ?? null, loggedFrom: r.logged_from ?? null,
  };
}
function mapEvent(r: any): AppEvent {
  return { id: r.id, patientId: r.patient_id ?? null, userId: r.user_id, deviceId: r.device_id ?? null, at: r.at, kind: r.kind, data: r.data || {} };
}
function mapDevice(r: any): AppDevice {
  return {
    userId: r.user_id, deviceId: r.device_id, displayName: r.display_name, platform: r.platform, model: r.model, appVersion: r.app_version,
    notifications: r.notifications, exactAlarms: r.exact_alarms, batteryOptimised: r.battery_optimised, pushToken: r.push_token,
    lastSeenAt: r.last_seen_at, lastSyncAt: r.last_sync_at, remindersUntil: r.reminders_until, alertPrefs: r.alert_prefs ?? null,
  };
}
